import { AfterViewInit, Component, inject, Signal, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, of, takeUntil } from 'rxjs';
import { fadeInOutAnimation } from '@newroom-connect/library/animations';
import { AuthError, AuthErrorName } from '@newroom-connect/library/errors';
import { ISystemMessage, ILoginInput } from '@newroom-connect/library/interfaces';
import { AuthService, SessionService, StateService, TranslationService, LoggingService, ToastService } from '@newroom-connect/library/services';
import { Level } from '@newroom-connect/library/enums';

import { DestroyableComponent } from '../../abstract/destroyable/destroyable.component';

@Component({
  animations: [fadeInOutAnimation],
  template: ''
})
export abstract class SignInBaseComponent extends DestroyableComponent implements AfterViewInit {
  protected readonly router = inject(Router);
  protected readonly authService = inject(AuthService);
  protected readonly sessionService = inject(SessionService);
  protected readonly stateService = inject(StateService);
  protected readonly translationService = inject(TranslationService);
  protected readonly loggingService = inject(LoggingService);
  protected readonly toastService = inject(ToastService);

  public confirmRegistrationMessage: Signal<ISystemMessage>;
  public signInErrorMessage?: string;
  public userNotConfirmed = false;
  protected loginEmail?: string;

  constructor() {
    super();

    // Preload the confirm registration message
    this.confirmRegistrationMessage = signal<ISystemMessage>({
      headline: this.translationService.translate('PAGES.SIGNUP.CONFIRM_EMAIL.HEADLINE'),
      message: this.translationService.translate('PAGES.SIGNUP.CONFIRM_EMAIL.MESSAGE'),
      icon: 'inbox'
    });
  }

  /**
   *
   */
  public ngAfterViewInit(): void {
    // Wrap session refresh into timeout to let Angular detect the change on the next browser MicroTask.
    setTimeout(() => {
      const userSession = this.sessionService.getUserSession();

      if (userSession?.needsRefresh()) {
        this.handleSessionRefresh();
      }

      this.stateService.setLoadingState(false);
    }, 0);
  }

  /**
   *
   * @param event
   * @param isCodeSignin
   */
  public handleLogin(event: ILoginInput, isCodeSignin = false): void {
    this.stateService.setLoadingState(true);

    this.loginEmail = event.email;
    // Call auth-api for login (for suite event login input contains the project slug - for studio no project slug since the intent is to manage all accesible projects)
    this.authService.login({ ...event, isCodeLogin: isCodeSignin }).pipe(
      takeUntil(this.destroy$),
      catchError(error => this.handleLoginError(error, isCodeSignin)),
      finalize(() => {
        this.stateService.setLoadingState(false);
      })
    ).subscribe(() => {
      this.onLoginSuccess();
    });
  }

  /**
   * Handle a resend confirmation event where the user will get a new confirmation email.
   */
  public handleResendConfirmation(): void {
    if (!this.loginEmail) {
      return;
    }
    this.stateService.setLoadingState(true);
    this.authService.resendConfirmation({ email: this.loginEmail }).pipe(
      takeUntil(this.destroy$),
      catchError(error => this.handleLoginError(error))
    ).subscribe(() => this.stateService.setLoadingState(false));
  }

  /**
   * Handle login error, clears user session and sets the signin error message.
   *
   * @param error
   * @param isCodeSignin
   *
   * @returns
   */
  protected handleLoginError(error: Error, isCodeSignin = false): Observable<void> {
    this.loggingService.error(`Failed to login with error ${error.name} ${error.message}`);

    if (!(error instanceof AuthError)) {
      this.setLoginFailedMessage(isCodeSignin);

      return of();
    }

    switch (error.name) {
      case AuthErrorName.USER_NOT_CONFIRMED:
        this.userNotConfirmed = true;

        break;
      case AuthErrorName.SESSION_EXPIRED:
        this.setLoginFailedMessage(isCodeSignin);
        this.sessionService.clearUserSession();
        this.stateService.setLoadingState(false);

        break;
      case AuthErrorName.INSUFFICIENT_PERMISSIONS:
        this.setLoginFailedMessage(isCodeSignin);
        this.toastService.setMessage({
          message: this.signInErrorMessage + this.translationService.translate('ERROR.INSUFFICIENT_PERMISSIONS.SUBLINE'),
          level: Level.ERROR
        });

        break;
      case AuthErrorName.INVALID_CREDENTIALS:
      default:
        this.setLoginFailedMessage(isCodeSignin);

        break;
    }

    return of();
  }

  /**
   * Set the generic login-failed error message and, for code sign-in, surface it as a toast.
   *
   * @param isCodeSignin Whether the failed login was a code sign-in.
   */
  private setLoginFailedMessage(isCodeSignin: boolean): void {
    this.signInErrorMessage = this.translationService.translate(isCodeSignin ? 'ERROR.CODE_LOGIN_FAILED.MESSAGE' : 'ERROR.LOGIN_FAILED.MESSAGE');

    // For the case of code sign in, we use the toast service to display a toaster message
    if (isCodeSignin) {
      this.toastService.setMessage({
        message: this.signInErrorMessage,
        level: Level.ERROR
      });
    }
  }

  /**
   * Handle session refresh, clears user session and sets the signin error message.
   *
   */
  protected handleSessionRefresh(): void {
    this.stateService.setLoadingState(true);

    this.authService.refreshSession().pipe(
      takeUntil(this.destroy$),
      finalize(() => this.stateService.setLoadingState(false))
    ).subscribe(() => this.onSessionRefreshed());
  }

  /**
   *
   */
  protected abstract onLoginSuccess(): void;

  /**
   *
   */
  protected abstract onSessionRefreshed(): void;
}
